/**
 * Utility functions for initializing timeline and filter data
 */

import { combinePassageAndBookEvents } from './map-helpers';
import { parseDateToYear } from './map-helpers';
import { PERIOD_DEFINITIONS } from '@/lib/utils/historical-periods-shared';

interface TimelineInitializationResult {
    minYear: number;
    maxYear: number;
    eras: Array<{ label: string; startYear: number; endYear: number; color: string }>;
    initialYear: number;
    placeTypes: Set<string>;
    eventCategories: Set<string>;
}

/**
 * Generate complete list of standard historical periods as timeline eras
 * Uses PERIOD_DEFINITIONS to ensure no gaps in the timeline
 *
 * @returns Array of timeline eras with colors
 */
export function getStandardHistoricalEras(): Array<{ label: string; startYear: number; endYear: number; color: string }> {
    const eraColors = [
        '#3b82f6', // blue
        '#8b5cf6', // purple
        '#ec4899', // pink
        '#f59e0b', // amber
        '#10b981', // green
        '#06b6d4', // cyan
        '#f97316', // orange
        '#6366f1', // indigo
    ];

    return Object.entries(PERIOD_DEFINITIONS).map(([period, def], index) => ({
        label: period,
        startYear: def.start,
        endYear: def.end,
        color: eraColors[index % eraColors.length]
    }));
}

/**
 * Extract unique historical contexts from places by matching entity names
 * Uses a Map-based lookup for O(1) performance
 *
 * @param entities - Geographic entities from passage
 * @param allPlaces - All places from the general database
 * @returns Array of historical contexts from matching places
 */
export function getHistoricalContextsFromPlaces(
    entities: GeographicEntity[],
    allPlaces: Place[]
): HistoricalContext[] {
    const contexts: HistoricalContext[] = [];

    // Build a lookup map once for O(1) performance
    const placeLookup = new Map<string, Place>();
    allPlaces.forEach(place => {
        // Index by placeName (case-insensitive)
        placeLookup.set(place.placeName.toLowerCase(), place);
        // Also index by alternate names
        place.alternateNames?.forEach(altName => {
            placeLookup.set(altName.toLowerCase(), place);
        });
    });

    // Do fast O(1) lookups for each entity
    entities.forEach(entity => {
        const matchingPlace = placeLookup.get(entity.placeName.toLowerCase());

        if (matchingPlace?.historicalPeriods) {
            matchingPlace.historicalPeriods.forEach(period => {
                contexts.push({
                    period: period.period,
                    eraLabel: period.period,
                    displayDateRange: period.dates,
                    sortableYearStart: parseDateToYear(period.dates, true),
                    sortableYearEnd: parseDateToYear(period.dates, false),
                    mapFact: period.description
                });
            });
        }
    });

    return contexts;
}

/**
 * Initialize timeline data and filter sets from passage places and events
 *
 * This function:
 * - Uses complete standard historical periods for the timeline (no gaps)
 * - Uses static time range from Early Bronze Age through Byzantine Period
 * - Determines the initial year (from passageDate or midpoint)
 * - Collects all place types
 * - Collects all event categories
 *
 * @param placesByPassage - Places data for the passage
 * @param historicalEventsByPassage - Passage-specific events
 * @param historicalEventsByBook - Book-level events
 * @param passageDate - Optional specific date for the passage
 * @returns Initialized timeline and filter data, or null if no places data
 */
export function initializeTimelineAndFilters(
    placesByPassage: PlacesByPassage | null,
    historicalEventsByPassage: HistoricalEventsByPassage | null,
    historicalEventsByBook: HistoricalEventsByBook | null,
    passageDate: number | null
): TimelineInitializationResult | null {
    if (!placesByPassage) return null;

    const { entities } = placesByPassage;
    const allEvents = combinePassageAndBookEvents(historicalEventsByPassage, historicalEventsByBook);

    // Use complete standard historical periods for the timeline
    // This ensures no gaps in the era list (e.g., Late Bronze Age → Iron Age I → Iron Age II → Persian Period)
    const eras = getStandardHistoricalEras();

    // Use static time range from the standard periods (Early Bronze Age through Byzantine Period)
    // This ensures consistent timeline range across all passages
    const minYear = eras[0].startYear; // Early Bronze Age start: -3300
    const maxYear = eras[eras.length - 1].endYear; // Byzantine Period end: 638

    // Calculate initial year
    const initialYear = passageDate ?? Math.floor((minYear + maxYear) / 2);

    // Collect place types
    const placeTypes = new Set(entities.map(e => e.type));

    // Collect event categories
    const eventCategories = new Set<string>();
    allEvents.forEach(event => {
        if (event.categories) {
            event.categories.forEach(cat => {
                eventCategories.add(cat.category);
            });
        }
    });

    return {
        minYear,
        maxYear,
        eras,
        initialYear,
        placeTypes,
        eventCategories
    };
}

